import { useEffect, useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { redeemGiftCard } from "@/lib/api/gift-cards";
import { centsToDollars } from "@dnceclub/shared";
import toast from "react-hot-toast";

interface RedeemGiftCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCode?: string;
}

export function RedeemGiftCardDialog({
  open,
  onOpenChange,
  initialCode,
}: RedeemGiftCardDialogProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [code, setCode] = useState(initialCode || "");

  // Update code when initialCode changes (e.g. from URL param)
  useEffect(() => {
    if (initialCode) {
      setCode(initialCode);
    }
  }, [initialCode]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setCode("");
    setError(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setError("Please enter a gift card code.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await redeemGiftCard(trimmedCode);
      const dollars = centsToDollars(result.amount);
      toast.success(
        `Gift card redeemed! $${dollars.toFixed(2)} added to your ${result.studio_name} studio credit.`
      );
      resetForm();
      setTimeout(() => {
        onOpenChange(false);
      }, 500);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to redeem gift card. Please check the code and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="gift-card-code" className="text-on-dark">
          Gift Card Code
        </Label>
        <Input
          id="gift-card-code"
          placeholder="Enter your gift card code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            if (error) setError(null);
          }}
          className="bg-card text-on-dark uppercase"
          autoFocus
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleOpenChange(false)}
          disabled={isSubmitting}
        >
          CANCEL
        </Button>
        <Button type="submit" disabled={isSubmitting || !code.trim()}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              REDEEMING...
            </>
          ) : (
            "REDEEM"
          )}
        </Button>
      </div>
    </form>
  );

  const title = "Redeem Gift Card";
  const description = "Enter your gift card code to add credit to your account.";

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-on-dark">{title}</DialogTitle>
            <DialogDescription className="text-on-dark-muted">
              {description}
            </DialogDescription>
          </DialogHeader>
          {formContent}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[90dvh] flex flex-col rounded-t-2xl bg-card pb-safe"
      >
        <SheetHeader className="text-left flex-shrink-0 pb-4">
          <SheetTitle className="text-on-dark">{title}</SheetTitle>
          <SheetDescription className="text-on-dark-muted">
            {description}
          </SheetDescription>
        </SheetHeader>
        {formContent}
      </SheetContent>
    </Sheet>
  );
}
